/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Customer Portal Page
 *
 * PORTAL: Customer-facing results view
 *
 * This is NOT a shell — it does NOT collect data.
 * The Portal is the read-only, customer-facing presentation of results:
 *  - Report summary and recommendation
 *  - Educational animations (curated by the expert in the Expert Shell)
 *  - Educational documents (curated by the expert in the Expert Shell)
 *  - Clean, branded, no editing controls
 *
 * Accessed via the existing /story/ui/:docId route (CustomerStoryPage).
 * This file provides the Portal UI components (animations, cards, layout).
 * It is NOT a separate route — it hardens the existing customer experience.
 * Content visibility is controlled by the ExperienceContext which the
 * Expert Shell populates.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useExperience, type EducationalContentItem } from '../context/ExperienceContext';
import { HeatPumpEnergyFlow } from '@system-rec/decision-ui';
import ErrorBoundary from '../components/ErrorBoundary';

// ─── Animation Renderer ─────────────────────────────────────────────

/**
 * Renders the appropriate animation component for a given content key.
 * New animations are registered here as they are built.
 */
function AnimationCard({ item }: { item: EducationalContentItem }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="portal__card portal__card--animation">
      <header className="portal__card-header">
        <h3 className="portal__card-title">{item.label}</h3>
        <button
          className="portal__card-expand"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Collapse' : 'Explore'}
        </button>
      </header>
      <p className="portal__card-desc">{item.description}</p>

      {expanded && (
        <div className="portal__card-content">
          <AnimationByKey contentKey={item.key} />
        </div>
      )}
    </article>
  );
}

function AnimationByKey({ contentKey }: { contentKey: string }) {
  switch (contentKey) {
    case 'anim-heat-pump-energy-flow':
      return <HeatPumpEnergyFlow animated />;

    case 'anim-y-plan-valve':
      return <YPlanValveAnimation />;

    case 'anim-s-plan-valve':
      return <SPlanValveAnimation />;

    case 'anim-sealed-vs-open-vented':
      return <SealedVsOpenAnimation />;

    case 'anim-hydraulic-separation':
      return <HydraulicSeparationAnimation />;

    case 'anim-power-flush':
      return <PowerFlushAnimation />;

    case 'anim-laminar-filter':
      return <LaminarFlowFilterAnimation />;

    case 'anim-weighing-comparison':
      return (
        <div className="portal__placeholder">
          Option comparison animation — available once your report is generated.
        </div>
      );

    default:
      return (
        <div className="portal__placeholder">
          Animation coming soon.
        </div>
      );
  }
}

// ─── SVG Animation Components ───────────────────────────────────────

function YPlanValveAnimation() {
  const [mode, setMode] = useState<'OFF' | 'HW_ONLY' | 'CH_ONLY' | 'MID_PORT'>('OFF');

  const STATES = {
    OFF:      { valveAngle: 0, pumpOn: false, boilerOn: false, flowColor: '#666', flowRate: 0 },
    HW_ONLY:  { valveAngle: 0, pumpOn: true, boilerOn: true, flowColor: '#ef4444', flowRate: 100 },
    CH_ONLY:  { valveAngle: 90, pumpOn: true, boilerOn: true, flowColor: '#f97316', flowRate: 100 },
    MID_PORT: { valveAngle: 45, pumpOn: true, boilerOn: true, flowColor: '#ec4899', flowRate: 60 }, // Reduced flow when split
  };

  const state = STATES[mode];

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 420 300" className="portal__anim-svg">
        <title>Y-Plan valve animation showing {mode} mode - Single Entry, Double Exit (Shared Path)</title>
        {/* Boiler */}
        <rect x="20" y="100" width="60" height="80" rx="6" fill={state.boilerOn ? '#fee2e2' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="50" y="145" textAnchor="middle" fontSize="11" fill="#333">Boiler</text>

        {/* Flow pipe: Boiler → Valve (shared inlet) */}
        <line x1="80" y1="140" x2="170" y2="140" stroke={state.pumpOn ? state.flowColor : '#ccc'} strokeWidth="6" strokeLinecap="round">
          {state.pumpOn && <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1s" repeatCount="indefinite" />}
        </line>

        {/* Shared path label */}
        <text x="125" y="130" textAnchor="middle" fontSize="9" fill="#dc2626" fontWeight="600">
          Single Entry Path
        </text>

        {/* Throttle indicator when both active */}
        {mode === 'MID_PORT' && (
          <g>
            <rect x="165" y="135" width="10" height="10" fill="none" stroke="#dc2626" strokeWidth="2" />
            <text x="165" y="160" fontSize="8" fill="#dc2626" fontWeight="600">Throttle</text>
            <text x="165" y="170" fontSize="7" fill="#dc2626">Narrow</text>
            <text x="165" y="178" fontSize="7" fill="#dc2626">Throat</text>
          </g>
        )}

        {/* Y-Valve body */}
        <circle cx="190" cy="140" r="20" fill="#e5e7eb" stroke="#666" strokeWidth="2" />
        <text x="190" y="144" textAnchor="middle" fontSize="10" fill="#333">Valve</text>
        {/* Valve position indicator */}
        <line
          x1="190" y1="140"
          x2={190 + 18 * Math.cos((state.valveAngle - 90) * Math.PI / 180)}
          y2={140 + 18 * Math.sin((state.valveAngle - 90) * Math.PI / 180)}
          stroke="#1d4ed8" strokeWidth="3" strokeLinecap="round"
          style={{ transition: 'all 0.6s ease' }}
        />

        {/* Pipe to Cylinder (Hot Water) — upward */}
        <line x1="190" y1="120" x2="190" y2="40" stroke={mode === 'HW_ONLY' || mode === 'MID_PORT' ? state.flowColor : '#ccc'} strokeWidth="6" strokeLinecap="round" />
        <rect x="165" y="10" width="50" height="35" rx="6" fill={mode === 'HW_ONLY' || mode === 'MID_PORT' ? '#dbeafe' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="190" y="28" textAnchor="middle" fontSize="10" fill="#333">Hot Water</text>
        <text x="190" y="38" textAnchor="middle" fontSize="9" fill="#666">Cylinder</text>

        {/* Flow rate indicator for HW */}
        {(mode === 'HW_ONLY' || mode === 'MID_PORT') && (
          <text x="210" y="70" fontSize="9" fill={mode === 'MID_PORT' ? '#dc2626' : '#059669'} fontWeight="600">
            {mode === 'MID_PORT' ? '60%' : '100%'}
          </text>
        )}

        {/* Pipe to Radiators (CH) — rightward */}
        <line x1="210" y1="140" x2="340" y2="140" stroke={mode === 'CH_ONLY' || mode === 'MID_PORT' ? state.flowColor : '#ccc'} strokeWidth="6" strokeLinecap="round" />
        <rect x="340" y="115" width="60" height="50" rx="4" fill={mode === 'CH_ONLY' || mode === 'MID_PORT' ? '#fef3c7' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="370" y="137" textAnchor="middle" fontSize="10" fill="#333">Radiators</text>
        <text x="370" y="148" textAnchor="middle" fontSize="9" fill="#666">(Heating)</text>

        {/* Flow rate indicator for CH */}
        {(mode === 'CH_ONLY' || mode === 'MID_PORT') && (
          <text x="280" y="130" fontSize="9" fill={mode === 'MID_PORT' ? '#dc2626' : '#059669'} fontWeight="600">
            {mode === 'MID_PORT' ? '60%' : '100%'}
          </text>
        )}

        {/* Pump indicator */}
        <circle cx="130" cy="140" r="10" fill={state.pumpOn ? '#22c55e' : '#d1d5db'} stroke="#666" strokeWidth="1.5" />
        <text x="130" y="144" textAnchor="middle" fontSize="8" fill="white" fontWeight="bold">P</text>

        {/* Flow rate label at pump */}
        {state.pumpOn && (
          <text x="130" y="165" textAnchor="middle" fontSize="8" fill={mode === 'MID_PORT' ? '#dc2626' : '#059669'} fontWeight="600">
            Water Traffic: {state.flowRate}%
          </text>
        )}

        {/* Return pipe */}
        <line x1="340" y1="240" x2="80" y2="240" stroke="#60a5fa" strokeWidth="4" strokeDasharray="8 4" />
        <text x="210" y="260" textAnchor="middle" fontSize="10" fill="#60a5fa">Return Path</text>

        {/* System type label */}
        <text x="210" y="285" textAnchor="middle" fontSize="11" fill="#666" fontWeight="700">
          Y-Plan: Single-Entry, Double-Exit
        </text>
      </svg>

      <div className="portal__anim-controls">
        <button onClick={() => setMode('OFF')} className={`portal__anim-btn ${mode === 'OFF' ? 'portal__anim-btn--active' : ''}`}>Off</button>
        <button onClick={() => setMode('HW_ONLY')} className={`portal__anim-btn ${mode === 'HW_ONLY' ? 'portal__anim-btn--active' : ''}`}>Hot Water</button>
        <button onClick={() => setMode('CH_ONLY')} className={`portal__anim-btn ${mode === 'CH_ONLY' ? 'portal__anim-btn--active' : ''}`}>Heating</button>
        <button onClick={() => setMode('MID_PORT')} className={`portal__anim-btn ${mode === 'MID_PORT' ? 'portal__anim-btn--active' : ''}`}>Both (Split)</button>
      </div>
      <p className="portal__anim-caption">
        {mode === 'OFF' && 'System is off. The valve is at rest. Y-Plan uses a single entry path that splits at the valve.'}
        {mode === 'HW_ONLY' && 'Valve directs all flow (100%) to the hot water cylinder. Full water traffic to hot water.'}
        {mode === 'CH_ONLY' && 'Valve directs all flow (100%) to the radiators. Full water traffic to heating.'}
        {mode === 'MID_PORT' && 'Both heating and hot water are active — water must share a narrow throat at the valve, reducing flow rate to 60% for each. This is the shared path bottleneck.'}
      </p>
    </div>
  );
}

function SPlanValveAnimation() {
  const [hwValve, setHwValve] = useState(false);
  const [chValve, setChValve] = useState(false);
  const pumpOn = hwValve || chValve;
  
  // Flow rates remain constant even when both are active (independent paths)
  const hwFlowRate = hwValve ? 100 : 0;
  const chFlowRate = chValve ? 100 : 0;

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 420 280" className="portal__anim-svg">
        <title>S-Plan valve animation - Independent Pipework Paths</title>
        {/* Boiler */}
        <rect x="20" y="80" width="60" height="70" rx="6" fill={pumpOn ? '#fee2e2' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="50" y="120" textAnchor="middle" fontSize="11" fill="#333">Boiler</text>

        {/* Main flow pipe */}
        <line x1="80" y1="115" x2="160" y2="115" stroke={pumpOn ? '#ef4444' : '#ccc'} strokeWidth="6" strokeLinecap="round" />

        {/* Manifold/Junction - now labeled as such */}
        <circle cx="160" cy="115" r="8" fill="#e5e7eb" stroke="#666" strokeWidth="2" />
        <text x="160" y="145" textAnchor="middle" fontSize="9" fill="#666" fontWeight="600">Manifold</text>
        <text x="160" y="155" textAnchor="middle" fontSize="8" fill="#666">(Split Point)</text>

        {/* HW Valve + Cylinder — top branch (INDEPENDENT PATH) */}
        <line x1="160" y1="115" x2="160" y2="50" stroke={hwValve ? '#ef4444' : '#ccc'} strokeWidth="5" />
        <text x="140" y="80" fontSize="9" fill="#059669" fontWeight="600">Path 1</text>
        <circle cx="160" cy="50" r="12" fill={hwValve ? '#bfdbfe' : '#e5e7eb'} stroke="#666" strokeWidth="2" />
        <text x="160" y="54" textAnchor="middle" fontSize="8" fill="#333">V1</text>
        <line x1="160" y1="38" x2="290" y2="38" stroke={hwValve ? '#ef4444' : '#ccc'} strokeWidth="5" />
        <rect x="290" y="15" width="65" height="46" rx="6" fill={hwValve ? '#dbeafe' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="322" y="35" textAnchor="middle" fontSize="10" fill="#333">Hot Water</text>
        <text x="322" y="46" textAnchor="middle" fontSize="9" fill="#666">Cylinder</text>

        {/* Flow rate indicator for HW */}
        {hwValve && (
          <text x="225" y="30" fontSize="9" fill="#059669" fontWeight="600">
            {hwFlowRate}% Flow
          </text>
        )}

        {/* CH Valve + Radiators — bottom branch (INDEPENDENT PATH) */}
        <line x1="160" y1="115" x2="160" y2="180" stroke={chValve ? '#f97316' : '#ccc'} strokeWidth="5" />
        <text x="140" y="150" fontSize="9" fill="#059669" fontWeight="600">Path 2</text>
        <circle cx="160" cy="180" r="12" fill={chValve ? '#fed7aa' : '#e5e7eb'} stroke="#666" strokeWidth="2" />
        <text x="160" y="184" textAnchor="middle" fontSize="8" fill="#333">V2</text>
        <line x1="160" y1="192" x2="290" y2="192" stroke={chValve ? '#f97316' : '#ccc'} strokeWidth="5" />
        <rect x="290" y="170" width="65" height="46" rx="4" fill={chValve ? '#fef3c7' : '#f3f4f6'} stroke="#666" strokeWidth="2" />
        <text x="322" y="190" textAnchor="middle" fontSize="10" fill="#333">Radiators</text>
        <text x="322" y="201" textAnchor="middle" fontSize="9" fill="#666">(Heating)</text>

        {/* Flow rate indicator for CH */}
        {chValve && (
          <text x="225" y="184" fontSize="9" fill="#059669" fontWeight="600">
            {chFlowRate}% Flow
          </text>
        )}

        {/* Pump */}
        <circle cx="120" cy="115" r="10" fill={pumpOn ? '#22c55e' : '#d1d5db'} stroke="#666" strokeWidth="1.5" />
        <text x="120" y="119" textAnchor="middle" fontSize="8" fill="white" fontWeight="bold">P</text>

        {/* Return paths (also independent) */}
        <line x1="290" y1="60" x2="80" y2="60" stroke={hwValve ? '#60a5fa' : '#e5e7eb'} strokeWidth="3" strokeDasharray="6 3" />
        <line x1="290" y1="215" x2="80" y2="215" stroke={chValve ? '#60a5fa' : '#e5e7eb'} strokeWidth="3" strokeDasharray="6 3" />
        <text x="185" y="240" textAnchor="middle" fontSize="9" fill="#60a5fa">Independent Return Paths</text>

        {/* System type label */}
        <text x="210" y="265" textAnchor="middle" fontSize="11" fill="#666" fontWeight="700">
          S-Plan: Twin Independent Paths
        </text>

        {/* Flow rate advantage indicator when both active */}
        {hwValve && chValve && (
          <g>
            <rect x="365" y="100" width="50" height="40" rx="4" fill="#dcfce7" stroke="#059669" strokeWidth="2" />
            <text x="390" y="115" textAnchor="middle" fontSize="9" fill="#059669" fontWeight="700">No</text>
            <text x="390" y="126" textAnchor="middle" fontSize="9" fill="#059669" fontWeight="700">Throttle!</text>
            <text x="390" y="136" textAnchor="middle" fontSize="8" fill="#059669">100% each</text>
          </g>
        )}
      </svg>

      <div className="portal__anim-controls">
        <label className="portal__anim-toggle">
          <input type="checkbox" checked={hwValve} onChange={() => setHwValve(!hwValve)} />
          Hot Water Valve (V1)
        </label>
        <label className="portal__anim-toggle">
          <input type="checkbox" checked={chValve} onChange={() => setChValve(!chValve)} />
          Heating Valve (V2)
        </label>
      </div>
      <p className="portal__anim-caption">
        S-Plan uses two completely independent pipe paths from the boiler manifold. Each circuit runs at full flow (100%) regardless of the other.
        {hwValve && chValve && ' Both valves open — simultaneous hot water and heating at full flow rate. No shared path bottleneck!'}
        {hwValve && !chValve && ' Only the hot water valve is open — 100% flow to cylinder.'}
        {!hwValve && chValve && ' Only the heating valve is open — 100% flow to radiators.'}
        {!hwValve && !chValve && ' Both valves closed — system off.'}
      </p>
    </div>
  );
}

function SealedVsOpenAnimation() {
  const [systemType, setSystemType] = useState<'sealed' | 'open'>('sealed');

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 400 280" className="portal__anim-svg">
        <title>{systemType === 'sealed' ? 'Sealed' : 'Open vented'} heating system</title>

        {/* Boiler */}
        <rect x="30" y="160" width="60" height="60" rx="6" fill="#fee2e2" stroke="#666" strokeWidth="2" />
        <text x="60" y="195" textAnchor="middle" fontSize="11" fill="#333">Boiler</text>

        {/* Main loop */}
        <line x1="90" y1="190" x2="300" y2="190" stroke="#ef4444" strokeWidth="5" />
        <line x1="300" y1="190" x2="300" y2="230" stroke="#ef4444" strokeWidth="5" />
        <line x1="300" y1="230" x2="90" y2="230" stroke="#60a5fa" strokeWidth="5" />
        <line x1="90" y1="230" x2="30" y2="230" stroke="#60a5fa" strokeWidth="3" />

        {/* Radiator */}
        <rect x="260" y="170" width="60" height="30" rx="4" fill="#fef3c7" stroke="#666" strokeWidth="2" />
        <text x="290" y="190" textAnchor="middle" fontSize="10" fill="#333">Rads</text>

        {systemType === 'sealed' ? (
          <>
            {/* Expansion vessel */}
            <ellipse cx="180" cy="140" rx="25" ry="18" fill="#e0e7ff" stroke="#4f46e5" strokeWidth="2" />
            <text x="180" y="144" textAnchor="middle" fontSize="9" fill="#4f46e5">EV</text>
            <line x1="180" y1="158" x2="180" y2="190" stroke="#4f46e5" strokeWidth="2" strokeDasharray="4 2" />

            {/* Pressure gauge */}
            <circle cx="130" cy="155" r="14" fill="white" stroke="#666" strokeWidth="2" />
            <text x="130" y="152" textAnchor="middle" fontSize="7" fill="#333">1.5</text>
            <text x="130" y="162" textAnchor="middle" fontSize="7" fill="#333">bar</text>
            <line x1="130" y1="169" x2="130" y2="190" stroke="#666" strokeWidth="1.5" />

            <text x="200" y="30" textAnchor="middle" fontSize="14" fill="#4f46e5" fontWeight="bold">Sealed System</text>
            <text x="200" y="50" textAnchor="middle" fontSize="10" fill="#666">Pressurised — no header tank needed</text>
          </>
        ) : (
          <>
            {/* Feed & Expansion tank (loft) */}
            <rect x="155" y="20" width="50" height="35" rx="4" fill="#dbeafe" stroke="#2563eb" strokeWidth="2" />
            <text x="180" y="42" textAnchor="middle" fontSize="9" fill="#2563eb">F&amp;E Tank</text>
            {/* Vent pipe */}
            <line x1="180" y1="55" x2="180" y2="190" stroke="#2563eb" strokeWidth="2" strokeDasharray="6 3" />
            {/* Open vent */}
            <line x1="200" y1="190" x2="200" y2="70" stroke="#93c5fd" strokeWidth="2" />
            <text x="220" y="80" fontSize="9" fill="#93c5fd">Open vent</text>

            <text x="200" y="110" textAnchor="middle" fontSize="14" fill="#2563eb" fontWeight="bold">Open Vented</text>
            <text x="200" y="125" textAnchor="middle" fontSize="10" fill="#666">Feed &amp; expansion tank in the loft</text>
          </>
        )}
      </svg>

      <div className="portal__anim-controls">
        <button onClick={() => setSystemType('sealed')} className={`portal__anim-btn ${systemType === 'sealed' ? 'portal__anim-btn--active' : ''}`}>Sealed</button>
        <button onClick={() => setSystemType('open')} className={`portal__anim-btn ${systemType === 'open' ? 'portal__anim-btn--active' : ''}`}>Open Vented</button>
      </div>
      <p className="portal__anim-caption">
        {systemType === 'sealed'
          ? 'A sealed system uses an expansion vessel and pressure gauge. No tanks in the loft.'
          : 'An open vented system uses a feed & expansion tank in the loft. If the pump speed is too high, water can "pump over" into the tank.'}
      </p>
    </div>
  );
}

function HydraulicSeparationAnimation() {
  const [primaryFlow, setPrimaryFlow] = useState(80);
  const [secondaryFlow, setSecondaryFlow] = useState(60);
  const ratio = secondaryFlow / Math.max(primaryFlow, 1);
  const mixColor = ratio > 1 ? '#7c3aed' : ratio < 0.8 ? '#ef4444' : '#f97316';

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 440 240" className="portal__anim-svg">
        <title>Hydraulic separation — primary vs secondary flow</title>

        {/* Primary loop (left) */}
        <text x="80" y="20" textAnchor="middle" fontSize="12" fill="#ef4444" fontWeight="bold">Primary</text>
        <rect x="20" y="80" width="50" height="50" rx="6" fill="#fee2e2" stroke="#666" strokeWidth="2" />
        <text x="45" y="110" textAnchor="middle" fontSize="10" fill="#333">Boiler</text>
        <line x1="70" y1="100" x2="160" y2="100" stroke="#ef4444" strokeWidth="5" />
        <line x1="160" y1="160" x2="70" y2="160" stroke="#60a5fa" strokeWidth="5" />
        <line x1="20" y1="130" x2="20" y2="160" stroke="#60a5fa" strokeWidth="3" />

        {/* Neutral zone (low-loss header) */}
        <rect x="160" y="70" width="30" height="110" rx="8" fill="#f3f4f6" stroke="#6b7280" strokeWidth="2" strokeDasharray="4 2" />
        <text x="175" y="200" textAnchor="middle" fontSize="9" fill="#6b7280">Neutral</text>
        <text x="175" y="212" textAnchor="middle" fontSize="9" fill="#6b7280">Zone</text>

        {/* Color mixing in neutral zone */}
        <rect x="164" y="95" width="22" height="30" rx="4" fill={mixColor} opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
        </rect>

        {/* Secondary loop (right) */}
        <text x="330" y="20" textAnchor="middle" fontSize="12" fill="#f97316" fontWeight="bold">Secondary</text>
        <line x1="190" y1="100" x2="320" y2="100" stroke={mixColor} strokeWidth="5" />
        <line x1="320" y1="160" x2="190" y2="160" stroke="#60a5fa" strokeWidth="5" />
        <rect x="320" y="80" width="55" height="50" rx="4" fill="#fef3c7" stroke="#666" strokeWidth="2" />
        <text x="347" y="110" textAnchor="middle" fontSize="10" fill="#333">Rads</text>

        {/* Flow rate labels */}
        <text x="115" y="90" textAnchor="middle" fontSize="10" fill="#ef4444">{primaryFlow}%</text>
        <text x="260" y="90" textAnchor="middle" fontSize="10" fill={mixColor}>{secondaryFlow}%</text>
      </svg>

      <div className="portal__anim-controls portal__anim-controls--stacked">
        <label className="portal__anim-slider-label">
          Primary flow: {primaryFlow}%
          <input type="range" min="20" max="100" value={primaryFlow} onChange={e => setPrimaryFlow(Number(e.target.value))} className="portal__anim-slider" />
        </label>
        <label className="portal__anim-slider-label">
          Secondary flow: {secondaryFlow}%
          <input type="range" min="20" max="100" value={secondaryFlow} onChange={e => setSecondaryFlow(Number(e.target.value))} className="portal__anim-slider" />
        </label>
      </div>
      <p className="portal__anim-caption">
        {secondaryFlow > primaryFlow
          ? 'Secondary demand exceeds primary supply — cool return water mixes into the flow, reducing radiator temperature. This explains lukewarm radiators even when the boiler is firing.'
          : secondaryFlow < primaryFlow * 0.8
            ? 'Primary flow exceeds secondary demand — excess heat recirculates. The boiler may short-cycle.'
            : 'Flows are balanced. The neutral zone is stable and both loops operate efficiently.'}
      </p>
    </div>
  );
}

function PowerFlushAnimation() {
  const [flushing, setFlushing] = useState(false);
  const [sludgeLevel, setSludgeLevel] = useState(100);
  const [flowDirection, setFlowDirection] = useState<'right' | 'left'>('right');

  const startFlush = () => {
    setFlushing(true);
    setSludgeLevel(100);
    let direction: 'right' | 'left' = 'right';
    const interval = setInterval(() => {
      // Alternate flow direction for bi-directional effect
      direction = direction === 'right' ? 'left' : 'right';
      setFlowDirection(direction);
      
      setSludgeLevel(prev => {
        if (prev <= 5) {
          clearInterval(interval);
          setFlushing(false);
          return 0;
        }
        return prev - 5;
      });
    }, 200);
  };

  // Calculate heat distribution - starts at 20% when full of sludge, fills to 100% when clean
  const heatCoverage = 20 + (80 * (1 - sludgeLevel / 100));

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 300 200" className="portal__anim-svg">
        <title>Power flush simulation</title>
        {/* Radiator outline */}
        <rect x="50" y="30" width="200" height="140" rx="8" fill="#f9fafb" stroke="#666" strokeWidth="2" />

        {/* Sludge fill (from bottom) - the "mountain" */}
        <rect
          x="54" y={30 + 136 * (1 - sludgeLevel / 100)}
          width="192"
          height={136 * (sludgeLevel / 100)}
          rx="4"
          fill="#78350f"
          opacity={0.6 + 0.4 * (sludgeLevel / 100)}
          style={{ transition: 'all 0.2s ease' }}
        />

        {/* Heat lines (top area only when sludge present, expands as sludge clears) */}
        <rect
          x="54" y="34"
          width="192"
          height={136 * (heatCoverage / 100)}
          rx="4"
          fill="url(#heat-gradient)"
          opacity={0.7}
          style={{ transition: 'all 0.2s ease' }}
        />

        {/* Fins */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <line key={i} x1={75 + i * 25} y1="35" x2={75 + i * 25} y2="165" stroke="#d1d5db" strokeWidth="1" />
        ))}

        <text x="150" y="110" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold" style={{ textShadow: '0 0 3px rgba(0,0,0,0.5)' }}>
          {sludgeLevel > 0 ? `${sludgeLevel}% sludge` : 'Clean'}
        </text>

        {/* Bi-directional flow arrows when flushing */}
        {flushing && (
          <>
            {flowDirection === 'right' ? (
              <>
                {/* Flow from left to right */}
                <line x1="10" y1="100" x2="50" y2="100" stroke="#2563eb" strokeWidth="4" markerEnd="url(#flush-arrow-right)">
                  <animate attributeName="x1" values="0;20;0" dur="0.4s" repeatCount="indefinite" />
                </line>
                <line x1="250" y1="100" x2="290" y2="100" stroke="#78350f" strokeWidth="4">
                  <animate attributeName="x2" values="280;300;280" dur="0.4s" repeatCount="indefinite" />
                </line>
              </>
            ) : (
              <>
                {/* Flow from right to left */}
                <line x1="290" y1="100" x2="250" y2="100" stroke="#2563eb" strokeWidth="4" markerEnd="url(#flush-arrow-left)">
                  <animate attributeName="x1" values="300;280;300" dur="0.4s" repeatCount="indefinite" />
                </line>
                <line x1="50" y1="100" x2="10" y2="100" stroke="#78350f" strokeWidth="4">
                  <animate attributeName="x2" values="20;0;20" dur="0.4s" repeatCount="indefinite" />
                </line>
              </>
            )}
          </>
        )}

        <defs>
          <marker id="flush-arrow-right" markerWidth="8" markerHeight="8" refX="4" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#2563eb" />
          </marker>
          <marker id="flush-arrow-left" markerWidth="8" markerHeight="8" refX="4" refY="3" orient="auto">
            <path d="M6,0 L0,3 L6,6 Z" fill="#2563eb" />
          </marker>
          <linearGradient id="heat-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.4" />
          </linearGradient>
        </defs>
      </svg>

      <div className="portal__anim-controls">
        <button
          onClick={startFlush}
          disabled={flushing}
          className={`portal__anim-btn ${flushing ? 'portal__anim-btn--active' : ''}`}
        >
          {flushing ? 'Flushing...' : sludgeLevel === 0 ? 'Flush again' : 'Start Power Flush'}
        </button>
        <button
          onClick={() => { setSludgeLevel(100); setFlushing(false); }}
          className="portal__anim-btn"
        >
          Reset
        </button>
      </div>
      <p className="portal__anim-caption">
        {sludgeLevel === 0
          ? 'Radiator is clean — full heat output restored from bottom to top.'
          : flushing
            ? 'High-velocity bi-directional water flow is breaking up the magnetite mountain and flushing it out.'
            : 'Magnetite sludge accumulates at the bottom like a mountain, blocking heat. Only the top 20% heats properly.'}
      </p>
    </div>
  );
}

function LaminarFlowFilterAnimation() {
  const [running, setRunning] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; phase: 'entry' | 'vortex' | 'separation' | 'dropped' | 'exited'; progress: number; angle: number; radius: number }>>([]);
  const [nextId, setNextId] = useState(0);

  const startAnimation = () => {
    setRunning(true);
    setParticles([]);
    setNextId(0);
  };

  const stopAnimation = () => {
    setRunning(false);
  };

  const resetAnimation = () => {
    setRunning(false);
    setParticles([]);
    setNextId(0);
  };

  // Animation loop - add particles and update their positions
  useState(() => {
    if (!running) return;
    
    const interval = setInterval(() => {
      setParticles(prev => {
        // Add new particle every 300ms
        const shouldAddParticle = Math.random() > 0.7;
        let newParticles = [...prev];
        
        if (shouldAddParticle && newParticles.length < 20) {
          newParticles.push({
            id: nextId,
            phase: 'entry',
            progress: 0,
            angle: Math.random() * 360,
            radius: 30 + Math.random() * 20,
          });
          setNextId(nextId + 1);
        }

        // Update all particles
        return newParticles
          .map(p => {
            const newProgress = p.progress + 0.02;
            
            if (p.phase === 'entry') {
              if (newProgress >= 1) {
                return { ...p, phase: 'vortex' as const, progress: 0, angle: Math.random() * 360 };
              }
              return { ...p, progress: newProgress };
            }
            
            if (p.phase === 'vortex') {
              if (newProgress >= 1) {
                return { ...p, phase: 'separation' as const, progress: 0 };
              }
              return { ...p, progress: newProgress, angle: p.angle + 5 };
            }
            
            if (p.phase === 'separation') {
              if (newProgress >= 1) {
                // 80% drop to sump, 20% exit as clean water
                if (Math.random() > 0.2) {
                  return { ...p, phase: 'dropped' as const, progress: 0 };
                } else {
                  return { ...p, phase: 'exited' as const, progress: 0 };
                }
              }
              return { ...p, progress: newProgress };
            }
            
            if (p.phase === 'dropped' || p.phase === 'exited') {
              if (newProgress >= 1) {
                return null; // Remove particle
              }
              return { ...p, progress: newProgress };
            }
            
            return p;
          })
          .filter((p): p is NonNullable<typeof p> => p !== null);
      });
    }, 50);

    return () => clearInterval(interval);
  });

  const getParticlePosition = (p: typeof particles[0]) => {
    const entryX = 20;
    const entryY = 60;
    const vortexCenterX = 150;
    const vortexCenterY = 100;
    const sumpX = 150;
    const sumpY = 230;
    const exitX = 150;
    const exitY = 20;

    switch (p.phase) {
      case 'entry': {
        // Turbulent entry - erratic path
        const x = entryX + (vortexCenterX - entryX) * p.progress + Math.sin(p.progress * 10) * 10;
        const y = entryY + (vortexCenterY - entryY) * p.progress + Math.cos(p.progress * 8) * 8;
        return { x, y, color: '#78350f', opacity: 0.8, size: 4 };
      }
      case 'vortex': {
        // Spiral motion
        const spiralProgress = p.progress;
        const currentRadius = p.radius * (1 - spiralProgress * 0.3);
        const x = vortexCenterX + currentRadius * Math.cos(p.angle * Math.PI / 180);
        const y = vortexCenterY + currentRadius * Math.sin(p.angle * Math.PI / 180) + spiralProgress * 40;
        return { x, y, color: '#78350f', opacity: 0.7, size: 4 };
      }
      case 'separation': {
        // Slow down and move toward outer walls - laminar flow (straight lines)
        const targetX = vortexCenterX + (p.angle > 180 ? -60 : 60);
        const targetY = vortexCenterY + 50;
        const x = vortexCenterX + (targetX - vortexCenterX) * p.progress;
        const y = vortexCenterY + 40 + (targetY - vortexCenterY - 40) * p.progress;
        return { x, y, color: '#a16207', opacity: 0.6, size: 3 };
      }
      case 'dropped': {
        // Fall into sump
        const startX = vortexCenterX + (p.angle > 180 ? -60 : 60);
        const startY = vortexCenterY + 50;
        const x = startX;
        const y = startY + (sumpY - startY) * p.progress;
        return { x, y, color: '#78350f', opacity: 0.5 - p.progress * 0.5, size: 3 };
      }
      case 'exited': {
        // Clean water exits top
        const x = exitX;
        const y = vortexCenterY - 40 - (vortexCenterY - 40 - exitY) * p.progress;
        return { x, y, color: '#3b82f6', opacity: 0.4 - p.progress * 0.4, size: 2 };
      }
      default:
        return { x: 0, y: 0, color: '#666', opacity: 0, size: 0 };
    }
  };

  return (
    <div className="portal__anim-wrapper">
      <svg viewBox="0 0 300 260" className="portal__anim-svg">
        <title>Laminar flow filter - cyclonic separation</title>
        
        {/* Filter canister outline */}
        <defs>
          <linearGradient id="canister-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f0f9ff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#e0f2fe" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {/* Canister body - narrow top, wide middle, narrow bottom */}
        <path
          d="M 110 20 L 140 50 L 80 90 L 80 180 L 100 220 L 200 220 L 220 180 L 220 90 L 160 50 L 190 20 Z"
          fill="url(#canister-fill)"
          stroke="#0369a1"
          strokeWidth="2"
        />

        {/* Zone labels */}
        <text x="250" y="60" fontSize="10" fill="#0369a1" fontWeight="600">Entry</text>
        <text x="250" y="65" fontSize="8" fill="#0369a1">(Turbulent)</text>
        <text x="250" y="110" fontSize="10" fill="#0369a1" fontWeight="600">Vortex</text>
        <text x="250" y="115" fontSize="8" fill="#0369a1">(Active)</text>
        <text x="250" y="160" fontSize="10" fill="#059669" fontWeight="600">Quiet</text>
        <text x="250" y="165" fontSize="8" fill="#059669">(Laminar)</text>
        <text x="250" y="230" fontSize="10" fill="#78350f" fontWeight="600">Sump</text>

        {/* Spiral guide lines (subtle) */}
        <path
          d="M 150 100 Q 180 100 180 130 Q 180 160 150 160 Q 120 160 120 130 Q 120 100 150 100"
          fill="none"
          stroke="#bae6fd"
          strokeWidth="1"
          strokeDasharray="4 2"
          opacity="0.4"
        />

        {/* Entry pipe */}
        <rect x="10" y="55" width="100" height="10" fill="#dbeafe" stroke="#0369a1" strokeWidth="1.5" />
        <text x="60" y="50" textAnchor="middle" fontSize="9" fill="#0369a1">Water in</text>
        <polygon points="15,60 25,65 15,70" fill="#ef4444" opacity="0.6">
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0,0; 20,0; 0,0"
            dur="1s"
            repeatCount="indefinite"
          />
        </polygon>

        {/* Exit pipe (clean water) */}
        <rect x="140" y="15" width="10" height="15" fill="#dbeafe" stroke="#0369a1" strokeWidth="1.5" />
        <text x="150" y="12" textAnchor="middle" fontSize="9" fill="#3b82f6">Clean out</text>

        {/* Sump collection */}
        <rect x="100" y="220" width="100" height="25" rx="4" fill="#78350f" opacity="0.4" stroke="#78350f" strokeWidth="1.5" />
        <text x="150" y="250" textAnchor="middle" fontSize="9" fill="#78350f">Debris Collection</text>

        {/* Animated particles */}
        {running && particles.map(p => {
          const pos = getParticlePosition(p);
          return (
            <circle
              key={p.id}
              cx={pos.x}
              cy={pos.y}
              r={pos.size}
              fill={pos.color}
              opacity={pos.opacity}
            />
          );
        })}

        {/* Static label overlay */}
        <text x="150" y="240" textAnchor="middle" fontSize="8" fill="#666" fontStyle="italic">
          "Like a bagless vacuum for your heating water"
        </text>
      </svg>

      <div className="portal__anim-controls">
        <button
          onClick={running ? stopAnimation : startAnimation}
          className={`portal__anim-btn ${running ? 'portal__anim-btn--active' : ''}`}
        >
          {running ? 'Pause' : 'Start Filter'}
        </button>
        <button onClick={resetAnimation} className="portal__anim-btn">
          Reset
        </button>
      </div>
      <p className="portal__anim-caption">
        {!running
          ? 'The filter uses quiet zones (laminar flow) and active zones (turbulent) to trap debris. Click "Start Filter" to see it work.'
          : 'Water enters at high velocity (turbulent), spins in a vortex, slows down into laminar flow as particles move to walls, then drops debris into the sump while clean water exits.'}
      </p>
    </div>
  );
}

// ─── Document Card ──────────────────────────────────────────────────

function DocumentCard({ item }: { item: EducationalContentItem }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="portal__card portal__card--document">
      <header className="portal__card-header">
        <h3 className="portal__card-title">{item.label}</h3>
        <button
          className="portal__card-expand"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Close' : 'Read'}
        </button>
      </header>
      <p className="portal__card-desc">{item.description}</p>
      {expanded && (
        <div className="portal__card-content portal__card-content--doc">
          <p className="portal__doc-placeholder">
            Full educational content for "{item.label}" will be loaded here.
            This document is curated by your heating engineer specifically for your property.
          </p>
        </div>
      )}
    </article>
  );
}

// ─── Main Portal Page ───────────────────────────────────────────────

function CustomerPortalPage() {
  const { enabledAnimations, enabledDocuments, enabledContent } = useExperience();

  return (
    <div className="portal">
      {/* ─── Portal Identity Banner ─── */}
      <header className="portal__banner">
        <div className="portal__banner-content">
          <span className="portal__banner-badge">PORTAL</span>
          <span className="portal__banner-label">
            Your personalised heating report
          </span>
        </div>
        <nav className="portal__banner-nav">
          <Link to="/" className="portal__back-link">
            Back to Home
          </Link>
        </nav>
      </header>

      {/* ─── Hero Section ─── */}
      <section className="portal__hero">
        <h1 className="portal__hero-title">Your Heating Report</h1>
        <p className="portal__hero-subtitle">
          Your engineer has prepared this personalised report with interactive
          animations and educational materials to help you understand your
          heating system and the available options.
        </p>
        {enabledContent.length === 0 && (
          <p className="portal__hero-empty">
            No content has been enabled for this report yet. Ask your engineer to
            configure the Portal content in the Expert Shell.
          </p>
        )}
      </section>

      <ErrorBoundary context="Customer Portal">
        {/* ─── Animations Section ─── */}
        {enabledAnimations.length > 0 && (
          <section className="portal__section">
            <h2 className="portal__section-title">Interactive Animations</h2>
            <p className="portal__section-desc">
              Tap "Explore" on any animation to see how your heating system works.
            </p>
            <div className="portal__grid">
              {enabledAnimations.map(item => (
                <AnimationCard key={item.key} item={item} />
              ))}
            </div>
          </section>
        )}

        {/* ─── Documents Section ─── */}
        {enabledDocuments.length > 0 && (
          <section className="portal__section">
            <h2 className="portal__section-title">Educational Guides</h2>
            <p className="portal__section-desc">
              Your engineer has selected these guides to help you understand
              the recommendations in your report.
            </p>
            <div className="portal__grid">
              {enabledDocuments.map(item => (
                <DocumentCard key={item.key} item={item} />
              ))}
            </div>
          </section>
        )}
      </ErrorBoundary>

      {/* ─── Footer ─── */}
      <footer className="portal__footer">
        <p className="portal__footer-text">
          Report generated by Atlas PHM. Content curated by your heating engineer.
        </p>
      </footer>
    </div>
  );
}

export default CustomerPortalPage;
